import { computed, onScopeDispose, ref, unref, watch } from 'vue'
import { useStore } from 'vuex'
import { formatInTimeZone } from 'date-fns-tz'
import { authFetch } from '@/composables/useApi'

// Session-wide cache: mcs_customer_id -> earliest 'yyyy-MM-dd' | null.
// Only ever populated with a *successful* fetch result. A transient
// failure is never written here so the next id-set change retries it.
const earliestCache = new Map()

/**
 * Date-picker bounds for a set of customers.
 *
 * minDate = the earliest transaction date across the given customers
 * (null when none have data -> pickers get no floor; the backend range
 * guard remains the real boundary). maxDate = today in Asia/Dubai,
 * re-evaluated periodically so a session open across midnight doesn't
 * stay pinned to yesterday.
 *
 * @param {Ref<Array<number>>|Array<number>} customerIdsRef
 */
export function useCustomerDataRange(customerIdsRef) {
    const store = useStore()
    const earliests = ref({})

    // Generation counter guarding against a stale, slow-to-resolve load()
    // call overwriting the result of a newer one (e.g. rapid customer
    // selection changes).
    let loadGen = 0

    const load = async (ids) => {
        const myGen = ++loadGen
        const missing = ids.filter((id) => id != null && !earliestCache.has(id))
        const results = {}
        await Promise.all(missing.map(async (id) => {
            try {
                const resp = await authFetch(
                    `${store.state.apiUrl}api/v1/customers/${id}/data-range`,
                    {},
                    store.state.user
                )
                if (!resp.ok) throw new Error(`data-range failed (${resp.status})`)
                const body = await resp.json()
                const value = body?.data?.earliest_transaction_date ?? null
                earliestCache.set(id, value)
                results[id] = value
            } catch (e) {
                console.error('useCustomerDataRange:', e)
                // Transient failure: record it for this call only, do NOT
                // poison the session cache so a future id-set change retries.
                results[id] = null
            }
        }))

        // A newer load() call has since started; let it win instead.
        if (myGen !== loadGen) return

        const next = {}
        ids.forEach((id) => {
            next[id] = earliestCache.has(id) ? earliestCache.get(id) : (results[id] ?? null)
        })
        earliests.value = next
    }

    watch(
        () => (unref(customerIdsRef) || []).join(','),
        () => {
            const ids = unref(customerIdsRef) || []
            if (ids.length) load(ids)
            else earliests.value = {}
        },
        { immediate: true }
    )

    const minDate = computed(() => {
        const dates = Object.values(earliests.value).filter(Boolean)
        return dates.length ? dates.reduce((a, b) => (a < b ? a : b)) : null
    })

    // Ticking ref so maxDate re-evaluates instead of freezing at whatever
    // "today" was when this composable first ran.
    const nowTick = ref(Date.now())
    const tick = setInterval(() => { nowTick.value = Date.now() }, 60000)
    onScopeDispose(() => clearInterval(tick))

    const maxDate = computed(() => {
        nowTick.value
        return formatInTimeZone(new Date(), 'Asia/Dubai', 'yyyy-MM-dd')
    })

    return { minDate, maxDate }
}
